/**
 * Validation utilities for authentication forms
 */

import type { ContactKind } from '@/types/user.types';

/** Login identifier kind detected from free-text input. */
export type IdentifierType = ContactKind;

/** One password-strength rule shown on the signup form. */
export interface PasswordRule {
  key: string;
  label: string;
  valid: boolean;
}

/** Password check result (`valid` + per-rule flags). */
export interface PasswordValidation {
  valid: boolean;
  rules: PasswordRule[];
}

/** True when the trimmed value has one `@` and a dotted domain (no spaces). */
export const isValidEmail = (email: string): boolean => {
  const value = email.trim();
  const at = value.indexOf('@');
  if (at <= 0 || at !== value.lastIndexOf('@')) return false;
  const domain = value.slice(at + 1);
  const dot = domain.lastIndexOf('.');
  return !value.includes(' ') && dot > 0 && dot < domain.length - 1;
};

/** True when 6–15 digits remain after stripping punctuation; allows `+`, spaces, dashes, parentheses. */
export const isValidPhone = (phone: string): boolean => {
  const digitsOnly = phone.replaceAll(/\D/g, '');

  if (digitsOnly.length < 6 || digitsOnly.length > 15) {
    return false;
  }

  const phoneRegex = /^[\d\s\-+()]+$/;
  return phoneRegex.test(phone);
};

/**
 * Detects the type of identifier (email or phone)
 */
export const detectIdentifierType = (value: string): IdentifierType => {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return 'unknown';
  }

  if (isValidEmail(trimmedValue)) {
    return 'email';
  }

  const hasLetters = /[a-zA-Z]/.test(trimmedValue);
  const digitsOnly = trimmedValue.replaceAll(/\D/g, '');

  if (!hasLetters && digitsOnly.length >= 3) {
    return 'phone';
  }

  return 'unknown';
};

/**
 * Formats a phone number to E.164 format for Firebase
 * Assumes French numbers if no country code provided
 */
export const formatPhoneNumber = (phone: string, defaultCountryCode = '+33'): string => {
  let cleaned = phone.trim();

  if (cleaned.startsWith('+')) {
    cleaned = '+' + cleaned.slice(1).replaceAll(/\D/g, '');
  } else {
    cleaned = cleaned.replaceAll(/\D/g, '');

    if (cleaned.startsWith('0')) {
      cleaned = defaultCountryCode + cleaned.slice(1);
    } else if (!cleaned.startsWith('+')) {
      cleaned = defaultCountryCode + cleaned;
    }
  }

  return cleaned;
};

/** Length, case, digit, and special-character rules for the signup strength meter. */
export const validatePassword = (password: string): PasswordValidation => {
  const rules: PasswordRule[] = [
    {
      key: 'length',
      label: 'Au moins 8 caractères',
      valid: password.length >= 8,
    },
    {
      key: 'upper',
      label: 'Au moins 1 lettre majuscule (A-Z)',
      valid: /[A-Z]/.test(password),
    },
    {
      key: 'lower',
      label: 'Au moins 1 lettre minuscule (a-z)',
      valid: /[a-z]/.test(password),
    },
    {
      key: 'digit',
      label: 'Au moins 1 chiffre (0-9)',
      valid: /\d/.test(password),
    },
    {
      key: 'special',
      label: 'Au moins 1 caractère spécial',
      valid: /[^A-Za-z\d]/.test(password),
    },
  ];

  return {
    valid: rules.every((r) => r.valid),
    rules,
  };
};

/** Trims, strips `<>`, and caps at 255 characters. */
export const sanitizeInput = (input: string): string => {
  return input.trim().replaceAll(/[<>]/g, '').slice(0, 255);
};
